import React, { useCallback, useRef, useState } from 'react';
import { Form, Badge, ListGroup, Spinner, Button, Modal, Row, Col } from 'react-bootstrap';
import { useDiagramStore } from '../store/diagramStore';
import { SCENARIO_COLORS, StepType } from '../types';
import { createEmptyScenario } from '../utils/scenarioConverter';

const STEP_TYPES: { value: StepType; label: string }[] = [
  { value: 'action',     label: 'Действие (прямоугольник)' },
  { value: 'decision',   label: 'Решение (ромб)' },
  { value: 'subprocess', label: 'Подпроцесс (двойные линии)' },
  { value: 'switch',     label: 'Выбор (шестиугольник)' },
  { value: 'data',       label: 'Данные (параллелограмм)' },
  { value: 'error',      label: 'Ошибка (красная полоса)' },
  { value: 'start',      label: 'Старт (круг зелёный)' },
  { value: 'end',        label: 'Конец (круг красный)' },
];

const ScenarioPanel: React.FC = () => {
  const scenarios = useDiagramStore(s => s.scenarios);
  const toggleScenario = useDiagramStore(s => s.toggleScenario);
  const reorderScenarios = useDiagramStore(s => s.reorderScenarios);
  const deleteScenario = useDiagramStore(s => s.deleteScenario);
  const createScenario = useDiagramStore(s => s.createScenario);
  const addStep = useDiagramStore(s => s.addStep);
  const loading = useDiagramStore(s => s.loading);

  // Drag & drop state
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [overIndex, setOverIndex] = useState<number | null>(null);
  const dragNode = useRef<number | null>(null);

  // Add scenario modal
  const [showAdd, setShowAdd] = useState(false);
  const [newId, setNewId] = useState('');
  const [newTitle, setNewTitle] = useState('');

  // Add step modal
  const [addStepScenarioId, setAddStepScenarioId] = useState<string | null>(null);
  const [stepLabel, setStepLabel] = useState('');
  const [stepType, setStepType] = useState<StepType>('action');
  const [stepLaneId, setStepLaneId] = useState('');
  const [stepDesc, setStepDesc] = useState('');

  // Delete confirm
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const handleDragStart = useCallback((e: React.DragEvent, index: number) => {
    dragNode.current = index;
    setDragIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setDragImage(e.currentTarget as HTMLElement, 20, 20);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent, index: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setOverIndex(index);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent, toIndex: number) => {
    e.preventDefault();
    const fromIndex = dragNode.current;
    if (fromIndex !== null && fromIndex !== toIndex) {
      reorderScenarios(fromIndex, toIndex);
    }
    setDragIndex(null);
    setOverIndex(null);
    dragNode.current = null;
  }, [reorderScenarios]);

  const handleDragEnd = useCallback(() => {
    setDragIndex(null);
    setOverIndex(null);
    dragNode.current = null;
  }, []);

  const handleCreate = useCallback(async () => {
    if (!newId.trim() || !newTitle.trim()) return;
    const color = SCENARIO_COLORS[scenarios.length % SCENARIO_COLORS.length];
    const diagram = createEmptyScenario(newId.trim(), newTitle.trim(), color);
    await createScenario(diagram);
    setShowAdd(false);
    setNewId('');
    setNewTitle('');
  }, [newId, newTitle, scenarios.length, createScenario]);

  const handleDelete = useCallback(async () => {
    if (!deleteId) return;
    await deleteScenario(deleteId);
    setDeleteId(null);
  }, [deleteId, deleteScenario]);

  const openAddStep = useCallback((scenarioId: string) => {
    const sc = scenarios.find(s => s.id === scenarioId);
    setAddStepScenarioId(scenarioId);
    setStepLabel('');
    setStepType('action');
    setStepLaneId(sc?.diagram.lanes[0]?.id || 'app');
    setStepDesc('');
  }, [scenarios]);

  const handleAddStep = useCallback(() => {
    if (!addStepScenarioId || !stepLabel.trim()) return;
    const sc = scenarios.find(s => s.id === addStepScenarioId);
    if (!sc) return;
    const idBase = addStepScenarioId.toLowerCase().replace(/[^a-z0-9]/g, '');
    const stepId = `${idBase}-s${sc.diagram.steps.length + 1}`;
    addStep(addStepScenarioId, {
      id: stepId,
      label: stepLabel.trim(),
      type: stepType,
      laneId: stepLaneId,
      description: stepDesc.trim() || undefined,
      order: sc.diagram.steps.length,
    });
    setAddStepScenarioId(null);
  }, [addStepScenarioId, stepLabel, stepType, stepLaneId, stepDesc, scenarios, addStep]);

  const deletingScenario = deleteId ? scenarios.find(s => s.id === deleteId) : null;

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <div className="d-flex align-items-center gap-2 px-2 py-2"
        style={{ borderBottom: '1px solid #eee', flexShrink: 0 }}>
        <small className="text-muted">
          {scenarios.filter(s => s.visible).length} / {scenarios.length}
        </small>
        <div style={{ flex: 1 }} />
        <Button size="sm" variant="outline-primary"
          style={{ fontSize: '0.7rem', padding: '2px 8px' }}
          onClick={() => {
            setNewId(`SC-${String(scenarios.length + 1).padStart(3, '0')}`);
            setNewTitle('');
            setShowAdd(true);
          }}>
          + Сценарий
        </Button>
      </div>

      {/* List */}
      <div style={{ flex: 1, overflowY: 'auto' }}>
        {loading ? (
          <div className="p-3 text-center">
            <Spinner size="sm" animation="border" className="me-2" />
            <small className="text-muted">Загрузка из БД...</small>
          </div>
        ) : scenarios.length === 0 ? (
          <div className="p-3 text-center text-muted" style={{ fontSize: '0.82rem' }}>
            Нет сценариев
          </div>
        ) : (
          <ListGroup variant="flush">
            {scenarios.map((sc, index) => (
              <ListGroup.Item
                key={sc.id}
                className="py-2 px-2"
                draggable
                onDragStart={(e) => handleDragStart(e, index)}
                onDragOver={(e) => handleDragOver(e, index)}
                onDrop={(e) => handleDrop(e, index)}
                onDragEnd={handleDragEnd}
                style={{
                  fontSize: '0.82rem',
                  opacity: dragIndex === index ? 0.4 : sc.visible ? 1 : 0.5,
                  cursor: 'grab',
                  borderTop: overIndex === index && dragIndex !== null && dragIndex !== index
                    ? '2px solid #2196f3'
                    : undefined,
                  transition: 'border-top 0.1s, opacity 0.1s',
                }}
              >
                <div className="d-flex align-items-center gap-2">
                  <span style={{
                    color: '#aaa', fontSize: 14, cursor: 'grab',
                    userSelect: 'none', flexShrink: 0, lineHeight: 1,
                  }}>
                    ⠿
                  </span>
                  <Form.Check
                    type="switch"
                    checked={sc.visible}
                    onChange={() => toggleScenario(sc.id)}
                    onClick={(e: React.MouseEvent) => e.stopPropagation()}
                    style={{ minHeight: 'unset' }}
                  />
                  <div style={{
                    width: 12, height: 12, borderRadius: '50%',
                    background: sc.color, flexShrink: 0,
                  }} />
                  <div className="flex-grow-1" style={{ minWidth: 0 }}>
                    <div className="fw-semibold text-truncate">{sc.title}</div>
                    <small className="text-muted">{sc.id}</small>
                  </div>
                  <Badge bg="secondary" style={{ fontSize: '0.65rem' }} title={`${sc.diagram.steps.length} шагов`}>
                    {sc.diagram.steps.length}
                  </Badge>
                  <Button size="sm" variant="outline-success"
                    style={{ fontSize: '0.65rem', padding: '1px 4px', lineHeight: 1 }}
                    onClick={(e) => { e.stopPropagation(); openAddStep(sc.id); }}
                    title="Добавить шаг">
                    +
                  </Button>
                  <Button size="sm" variant="outline-danger"
                    style={{ fontSize: '0.65rem', padding: '1px 4px', lineHeight: 1 }}
                    onClick={(e) => { e.stopPropagation(); setDeleteId(sc.id); }}
                    title="Удалить">
                    &times;
                  </Button>
                </div>
              </ListGroup.Item>
            ))}
          </ListGroup>
        )}
      </div>

      {/* Add Scenario Modal */}
      <Modal show={showAdd} onHide={() => setShowAdd(false)} size="sm" centered>
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: '1rem' }}>Новый сценарий</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row className="g-2">
            <Col xs={12}>
              <Form.Label className="small mb-1">ID сценария</Form.Label>
              <Form.Control size="sm" value={newId}
                onChange={e => setNewId(e.target.value)}
                placeholder="SC-012" />
            </Col>
            <Col xs={12}>
              <Form.Label className="small mb-1">Название</Form.Label>
              <Form.Control size="sm" value={newTitle}
                onChange={e => setNewTitle(e.target.value)}
                placeholder="Название процесса"
                onKeyDown={e => { if (e.key === 'Enter') handleCreate(); }} />
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button size="sm" variant="secondary" onClick={() => setShowAdd(false)}>Отмена</Button>
          <Button size="sm" variant="primary" onClick={handleCreate}
            disabled={!newId.trim() || !newTitle.trim()}>
            Создать
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Add Step Modal */}
      {(() => {
        const sc = addStepScenarioId ? scenarios.find(s => s.id === addStepScenarioId) : null;
        return (
          <Modal show={!!addStepScenarioId} onHide={() => setAddStepScenarioId(null)} size="sm" centered>
            <Modal.Header closeButton>
              <Modal.Title style={{ fontSize: '1rem' }}>
                Новый шаг — {sc?.title}
              </Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <Row className="g-2">
                <Col xs={12}>
                  <Form.Label className="small mb-1">Название *</Form.Label>
                  <Form.Control size="sm" value={stepLabel}
                    onChange={e => setStepLabel(e.target.value)}
                    placeholder="Например: Отправить OTP"
                    onKeyDown={e => { if (e.key === 'Enter') handleAddStep(); }} />
                </Col>
                <Col xs={12}>
                  <Form.Label className="small mb-1">Тип фигуры</Form.Label>
                  <Form.Select size="sm" value={stepType}
                    onChange={e => setStepType(e.target.value as StepType)}>
                    {STEP_TYPES.map(t => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </Form.Select>
                </Col>
                <Col xs={12}>
                  <Form.Label className="small mb-1">Зона ответственности</Form.Label>
                  <Form.Select size="sm" value={stepLaneId}
                    onChange={e => setStepLaneId(e.target.value)}>
                    {(sc?.diagram.lanes || []).map(lane => (
                      <option key={lane.id} value={lane.id}>{lane.label}</option>
                    ))}
                  </Form.Select>
                </Col>
                <Col xs={12}>
                  <Form.Label className="small mb-1">Описание (опционально)</Form.Label>
                  <Form.Control size="sm" as="textarea" rows={2} value={stepDesc}
                    onChange={e => setStepDesc(e.target.value)}
                    placeholder="Подробное описание шага..." />
                </Col>
              </Row>
            </Modal.Body>
            <Modal.Footer>
              <Button size="sm" variant="secondary" onClick={() => setAddStepScenarioId(null)}>Отмена</Button>
              <Button size="sm" variant="primary" onClick={handleAddStep}
                disabled={!stepLabel.trim()}>
                Добавить
              </Button>
            </Modal.Footer>
          </Modal>
        );
      })()}

      {/* Delete Confirm Modal */}
      <Modal show={!!deleteId} onHide={() => setDeleteId(null)} size="sm" centered>
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: '1rem' }}>Удаление сценария</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ fontSize: '0.85rem' }}>
          Удалить сценарий <strong>{deletingScenario?.title}</strong> ({deleteId})?
          <br />
          <small className="text-danger">Это действие необратимо — сценарий будет удалён из базы данных.</small>
        </Modal.Body>
        <Modal.Footer>
          <Button size="sm" variant="secondary" onClick={() => setDeleteId(null)}>Отмена</Button>
          <Button size="sm" variant="danger" onClick={handleDelete}>Удалить</Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ScenarioPanel;
